import { useRegistrant } from '@/hooks/useRegistrant'
import { Participant } from '@/interfaces/participant'
import { InviteType } from '@/interfaces/invite'
import { useEffect, useState } from 'react'
import { InvitesHelper } from '@/helpers'
import useSwr from 'swr'
import { config } from '@/config'
import { InviteController } from '@/controllers'
import { Utils } from '@/lib/utils'
import { StorageFactory } from '@/services'
import { StorageType } from '@/interfaces/storage'

export interface SentRecipient {
  id?: number
  name: string
  address: string
  participants: Participant[]
  type: InviteType
  createdAt: string
}

const key = config.api.endpoints.invite.list

export const useSentRecipients = () => {
  const sessionStorage = StorageFactory.getStorage(StorageType.SESSION)
  const { data, mutate, isValidating, isLoading } = useSwr(key, InviteController.list)
  const { registrant } = useRegistrant()
  const [recipients, setRecipients] = useState<SentRecipient[]>([])
  const cacheKey = `alreadySentRecipients_${registrant?.id}`

  useEffect(() => {
    let recipients: SentRecipient[] = []
    const invites = data?.result?.[1]

    if (invites) {
      recipients = invites.map((invite) => ({
        name: invite.name,
        address:
          Utils.toLowerCase(invite.email) ||
          Utils.formatPhoneNumber(invite.phone) ||
          Utils.toLowerCase(invite.fingerprint),
        participants: invite.participants,
        type: invite.type,
        createdAt: invite.createdAt,
        id: invite.id,
      }))
    }

    const recipientsFromStorage = sessionStorage.getItem<SentRecipient[]>(cacheKey) || []
    const mergedRecipients = InvitesHelper.mergeRecipients(recipients, recipientsFromStorage)

    setRecipients(mergedRecipients)
  }, [data, cacheKey])

  const getInvitesByParticipant = (participant: Participant) =>
    InvitesHelper.getInvitesByParticipant(participant, recipients)
  const invitesWithoutVisits = recipients.filter((recipient) => recipient.type !== InviteType.VISIT)

  const addRecipient = (newRecipient: SentRecipient) => {
    const mergedRecipients = InvitesHelper.mergeRecipients(recipients, [newRecipient])

    sessionStorage.setItem(cacheKey, mergedRecipients)
    setRecipients(mergedRecipients)
  }

  return {
    isValidating,
    isLoading,
    recipients,
    invitesWithoutVisits,
    invitesWithoutVisitsCount: invitesWithoutVisits.length,
    getInvitesByParticipant,
    addRecipient,
    revalidate: mutate,
  }
}
